import { useState, useEffect } from 'react';
import {
  View, Text, TextInput, TouchableOpacity,
  StyleSheet, ScrollView, ActivityIndicator, Alert,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '@/hooks/useAuth';
import { useWorkoutRoutines } from '@/hooks/useWorkoutRoutines';
import { useOnboardingSuggestion } from '@/hooks/useOnboardingSuggestion';
import { supabase } from '@/lib/supabase';
import { OnboardingPlanDay, OnboardingPlanExercise, Student } from '@/lib/types';

interface EditableExercise extends OnboardingPlanExercise { }

interface EditableDay {
  name: string;
  exercises: EditableExercise[];
}

function buildEditableDays(days: OnboardingPlanDay[]): EditableDay[] {
  return days.map(day => ({
    name: day.name,
    exercises: day.exercises.map(ex => ({ ...ex })),
  }));
}

const GOAL_LABEL: Record<string, string> = {
  hypertrophy: '💪 Hipertrofia',
  weight_loss: '🔥 Emagrecimento',
  conditioning: '🏃 Condicionamento',
  health: '❤️ Saúde geral',
};

export default function NewFromSuggestionScreen() {
  const { suggestionId, studentId } = useLocalSearchParams<{ suggestionId: string; studentId: string }>();
  const { profile } = useAuth();
  const router = useRouter();
  const { createRoutine } = useWorkoutRoutines(studentId ?? '');
  const { suggestion, loading: suggLoading } = useOnboardingSuggestion(studentId ?? '');
  const [student, setStudent] = useState<Student | null>(null);
  const [routineName, setRoutineName] = useState('');
  const [days, setDays] = useState<EditableDay[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!studentId) return;
    supabase.from('students').select('*').eq('id', studentId).single()
      .then(({ data, error }) => {
        if (error || !data) return;
        setStudent(data as Student);
      });
  }, [studentId]);

  useEffect(() => {
    if (!suggestion) return;
    setDays(buildEditableDays(suggestion.plan_json.days));
    const od = student?.onboarding_data;
    const goalLabel = od?.goal ? (GOAL_LABEL[od.goal] ?? od.goal) : 'Treino';
    setRoutineName(`Plano ${goalLabel} — IA`);
  }, [suggestion, student]);

  function updateReps(dayIdx: number, exIdx: number, value: string) {
    setDays(prev => prev.map((d, di) =>
      di !== dayIdx ? d : {
        ...d,
        exercises: d.exercises.map((ex, ei) =>
          ei !== exIdx ? ex : { ...ex, reps: value }
        ),
      }
    ));
  }

  function updateSets(dayIdx: number, exIdx: number, value: string) {
    const n = parseInt(value, 10);
    if (isNaN(n) || n < 1) return;
    setDays(prev => prev.map((d, di) =>
      di !== dayIdx ? d : {
        ...d,
        exercises: d.exercises.map((ex, ei) =>
          ei !== exIdx ? ex : { ...ex, sets: n }
        ),
      }
    ));
  }

  function removeExercise(dayIdx: number, exIdx: number) {
    setDays(prev => prev.map((d, di) =>
      di !== dayIdx ? d : { ...d, exercises: d.exercises.filter((_, ei) => ei !== exIdx) }
    ));
  }

  async function handleSave() {
    if (!routineName.trim() || !profile || !suggestion) return;
    setSaving(true);
    try {
      const routine = await createRoutine({ name: routineName.trim(), personal_id: profile.id });
      for (const day of days) {
        if (day.exercises.length === 0) continue;
        const { data: plan, error: planErr } = await supabase
          .from('workout_plans')
          .insert({ name: day.name, student_id: studentId, personal_id: profile.id, routine_id: routine.id })
          .select()
          .single();
        if (planErr) throw planErr;
        for (let i = 0; i < day.exercises.length; i++) {
          const ex = day.exercises[i];
          const { error: exErr } = await supabase.from('plan_exercises').insert({
            plan_id: plan.id,
            exercise_id: ex.exercise_id,
            sets: ex.sets,
            reps: parseInt(ex.reps, 10) || 12,
            suggested_load: null,
            order: i,
          });
          if (exErr) throw exErr;
        }
      }
      await supabase
        .from('onboarding_suggestions')
        .update({ status: 'accepted' })
        .eq('id', suggestionId);
      router.replace(`/(personal)/routines/${routine.id}` as any);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Tente novamente';
      Alert.alert('Erro ao salvar', message);
    } finally {
      setSaving(false);
    }
  }

  if (suggLoading || !suggestion) {
    return <ActivityIndicator style={{ flex: 1 }} color="#C8102E" />;
  }

  const od = student?.onboarding_data;

  return (
    <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">

        {student && (
          <View style={styles.studentCard}>
            <View style={styles.avatar}>
              <Text style={styles.initials}>
                {(student.full_name ?? student.email).split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()}
              </Text>
            </View>
            <View>
              <Text style={styles.studentName}>{student.full_name ?? student.email}</Text>
              {od && (
                <>
                  <Text style={styles.studentMeta}>
                    {od.level === 'beginner' ? '🌱 Iniciante' : od.level === 'intermediate' ? '⚡ Intermediário' : '🏆 Avançado'}
                    {' · '}
                    {GOAL_LABEL[od.goal] ?? od.goal}
                  </Text>
                  <Text style={styles.studentMeta}>📅 {od.days_per_week}×/semana</Text>
                </>
              )}
            </View>
          </View>
        )}

        <View style={styles.aiBlock}>
          <View style={styles.aiHeader}>
            <Text style={styles.aiIcon}>🤖</Text>
            <Text style={styles.aiLabel}>Análise da IA</Text>
          </View>
          <Text style={styles.aiSummary}>{suggestion.summary}</Text>
        </View>

        <Text style={styles.fieldLabel}>Nome da rotina</Text>
        <TextInput
          style={styles.input}
          value={routineName}
          onChangeText={setRoutineName}
          placeholder="Nome da rotina"
        />

        {days.map((day, di) => (
          <View key={di} style={styles.dayCard}>
            <Text style={styles.dayTitle}>{day.name}</Text>
            {day.exercises.map((ex, ei) => (
              <View key={ei} style={styles.exRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.exName}>{ex.name}</Text>
                  <View style={styles.exControls}>
                    <TextInput
                      style={styles.exInput}
                      value={String(ex.sets)}
                      onChangeText={v => updateSets(di, ei, v)}
                      keyboardType="numeric"
                      maxLength={2}
                    />
                    <Text style={styles.exSep}>×</Text>
                    <TextInput
                      style={styles.exInput}
                      value={ex.reps}
                      onChangeText={v => updateReps(di, ei, v)}
                      maxLength={8}
                    />
                  </View>
                </View>
                <TouchableOpacity onPress={() => removeExercise(di, ei)} style={styles.removeBtn}>
                  <Ionicons name="trash-outline" size={16} color="#C8102E" />
                </TouchableOpacity>
              </View>
            ))}
          </View>
        ))}

      </ScrollView>

      <View style={styles.footer}>
        <TouchableOpacity style={[styles.saveBtn, saving && styles.saveBtnDisabled]} onPress={handleSave} disabled={saving}>
          {saving ? <ActivityIndicator color="#fff" /> : <Text style={styles.saveBtnText}>Salvar Rotina</Text>}
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#F5F5F5' },
  content: { padding: 16, paddingBottom: 24 },
  studentCard: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: '#fff', borderRadius: 12, padding: 12, marginBottom: 12, borderWidth: 1, borderColor: '#e0e0e0' },
  avatar: { width: 44, height: 44, borderRadius: 22, backgroundColor: '#C8102E', alignItems: 'center', justifyContent: 'center' },
  initials: { color: '#fff', fontWeight: '700', fontSize: 15 },
  studentName: { fontSize: 14, fontWeight: '700', color: '#1A1A1A' },
  studentMeta: { fontSize: 11, color: '#888', marginTop: 1 },
  aiBlock: { backgroundColor: '#fff3f5', borderWidth: 1.5, borderColor: '#C8102E', borderRadius: 12, padding: 12, marginBottom: 14 },
  aiHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 },
  aiIcon: { fontSize: 15 },
  aiLabel: { fontSize: 12, fontWeight: '700', color: '#C8102E' },
  aiSummary: { fontSize: 12, color: '#444', lineHeight: 18 },
  fieldLabel: { fontSize: 12, fontWeight: '600', color: '#555', marginBottom: 6 },
  input: { backgroundColor: '#fff', borderWidth: 1, borderColor: '#ddd', borderRadius: 8, padding: 12, fontSize: 14, marginBottom: 14, color: '#1A1A1A' },
  dayCard: { backgroundColor: '#fff', borderRadius: 10, padding: 12, marginBottom: 10, borderWidth: 1, borderColor: '#e0e0e0' },
  dayTitle: { fontSize: 12, fontWeight: '700', color: '#C8102E', marginBottom: 8 },
  exRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, borderTopWidth: 1, borderTopColor: '#f0f0f0' },
  exName: { fontSize: 12, color: '#1A1A1A', marginBottom: 4 },
  exControls: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  exInput: { backgroundColor: '#f5f5f5', borderWidth: 1, borderColor: '#ddd', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4, fontSize: 13, width: 44, textAlign: 'center', color: '#1A1A1A' },
  exSep: { fontSize: 14, color: '#888' },
  removeBtn: { padding: 8 },
  footer: { padding: 16, paddingBottom: 32, backgroundColor: '#F5F5F5' },
  saveBtn: { backgroundColor: '#C8102E', borderRadius: 10, padding: 15, alignItems: 'center' },
  saveBtnDisabled: { opacity: 0.5 },
  saveBtnText: { color: '#fff', fontWeight: '700', fontSize: 16 },
});
